<script lang="ts" setup>
definePageMeta({
  layout: 'landing',
  smoothScroll: true,
  // Force a fresh component instance per slug — otherwise Vue Router reuses
  // this page across /business-types/barber -> /business-types/salon (same
  // matched route, only params differ), and the setup() below (which reads
  // route.params.slug once, synchronously) would never re-run.
  key: route => route.fullPath,
})

const route = useRoute()
const slug = route.params.slug as string

const type = useBusinessType(slug)
if (!type) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found' })
}

const { t, tm, rt } = useI18n()

// Icons live here rather than in lang/*.json so both locale files don't have to
// duplicate the same icon identifiers for every business type.
const ICON_SETS: Record<string, { problems: string[], features: string[] }> = {
  salon: {
    problems: ['i-heroicons-phone-20-solid', 'i-heroicons-clock-20-solid', 'i-heroicons-clipboard-document-list-20-solid'],
    features: ['i-heroicons-calendar-days-20-solid', 'i-heroicons-user-group-20-solid', 'i-heroicons-clock-20-solid', 'i-heroicons-bell-alert-20-solid', 'i-heroicons-clipboard-document-list-20-solid', 'i-heroicons-credit-card-20-solid'],
  },
  barber: {
    problems: ['i-heroicons-clock-20-solid', 'i-heroicons-user-group-20-solid', 'i-heroicons-bell-alert-20-solid'],
    features: ['i-heroicons-calendar-days-20-solid', 'i-heroicons-user-group-20-solid', 'i-heroicons-clock-20-solid', 'i-heroicons-bell-alert-20-solid', 'i-heroicons-chart-bar-20-solid', 'i-heroicons-credit-card-20-solid'],
  },
  massage: {
    problems: ['i-heroicons-clock-20-solid', 'i-heroicons-clipboard-document-list-20-solid', 'i-heroicons-bell-alert-20-solid'],
    features: ['i-heroicons-calendar-days-20-solid', 'i-heroicons-clock-20-solid', 'i-heroicons-clipboard-document-list-20-solid', 'i-heroicons-bell-alert-20-solid', 'i-heroicons-arrow-path-20-solid', 'i-heroicons-credit-card-20-solid'],
  },
  nails: {
    problems: ['i-heroicons-clipboard-document-list-20-solid', 'i-heroicons-clock-20-solid', 'i-heroicons-bell-alert-20-solid'],
    features: ['i-heroicons-sparkles-20-solid', 'i-heroicons-clock-20-solid', 'i-heroicons-user-group-20-solid', 'i-heroicons-bell-alert-20-solid', 'i-heroicons-credit-card-20-solid', 'i-heroicons-clipboard-document-list-20-solid'],
  },
  'pet-grooming': {
    problems: ['i-heroicons-clock-20-solid', 'i-heroicons-clipboard-document-list-20-solid', 'i-heroicons-bell-alert-20-solid'],
    features: ['i-mdi-paw', 'i-heroicons-clock-20-solid', 'i-heroicons-user-group-20-solid', 'i-heroicons-bell-alert-20-solid', 'i-heroicons-clipboard-document-list-20-solid', 'i-heroicons-credit-card-20-solid'],
  },
  'spa-sauna': {
    problems: ['i-heroicons-clock-20-solid', 'i-heroicons-credit-card-20-solid', 'i-heroicons-user-group-20-solid'],
    features: ['i-heroicons-calendar-days-20-solid', 'i-heroicons-user-group-20-solid', 'i-heroicons-credit-card-20-solid', 'i-heroicons-gift-20-solid', 'i-heroicons-bell-alert-20-solid', 'i-heroicons-chart-bar-20-solid'],
  },
  physiotherapy: {
    problems: ['i-heroicons-arrow-path-20-solid', 'i-heroicons-clipboard-document-list-20-solid', 'i-heroicons-clock-20-solid'],
    features: ['i-heroicons-arrow-path-20-solid', 'i-heroicons-calendar-days-20-solid', 'i-heroicons-clipboard-document-list-20-solid', 'i-heroicons-chart-bar-20-solid', 'i-heroicons-bell-alert-20-solid', 'i-heroicons-credit-card-20-solid'],
  },
  'medical-center': {
    problems: ['i-heroicons-phone-20-solid', 'i-heroicons-clock-20-solid', 'i-heroicons-clipboard-document-list-20-solid'],
    features: ['i-heroicons-user-group-20-solid', 'i-heroicons-calendar-days-20-solid', 'i-heroicons-clock-20-solid', 'i-heroicons-bell-alert-20-solid', 'i-heroicons-clipboard-document-list-20-solid', 'i-heroicons-credit-card-20-solid'],
  },
  gym: {
    problems: ['i-heroicons-user-group-20-solid', 'i-heroicons-credit-card-20-solid', 'i-heroicons-clock-20-solid'],
    features: ['i-heroicons-calendar-days-20-solid', 'i-heroicons-user-group-20-solid', 'i-heroicons-credit-card-20-solid', 'i-heroicons-arrow-path-20-solid', 'i-heroicons-bell-alert-20-solid', 'i-heroicons-chart-bar-20-solid'],
  },
}

function items(path: string) {
  return (tm(`page.business_types.${type.slug}.${path}`) as Record<string, unknown>[])
    .map(item => Object.fromEntries(Object.entries(item).map(([k, v]) => [k, rt(v)])))
}

const problemItems = computed(() => items('problems.items').map((item, i) => ({ ...item, icon: ICON_SETS[type.slug]?.problems[i] })))
const featureItems = computed(() => items('features.items').map((item, i) => ({ ...item, icon: ICON_SETS[type.slug]?.features[i] })))
const stepItems = computed(() => items('steps.items').map((item, i) => ({ ...item, number: i + 1 })))
const outcomeItems = computed(() => items('outcomes.items').map(item => ({ ...item, icon: 'i-heroicons-check-circle-20-solid' })))

const typeName = computed(() => t(`page.home.one_platform.business_types.${type.i18nKey}`))
const seoTitleKey = type.built ? `page.business_types.${type.slug}.meta.title` : `page.home.one_platform.business_types.${type.i18nKey}`

// Labels reuse the keys LandingNav links these destinations with, so a crumb
// never disagrees with the page it points at. Empty for unbuilt types: the
// coming-soon view shows no trail, and markup without a visible trail is a
// structured-data violation.
const { crumbs } = useBreadcrumbs(
  type.built
    ? [
        { label: t('label.home'), to: '/' },
        { label: t('page.home.services'), to: '/business-types' },
        { label: typeName.value },
      ]
    : [],
)

useHead({
  titleTemplate: chunk => chunk || 'bookforme',
  title: () => seoTitle(t, seoTitleKey, typeName.value),
  meta: [
    { name: 'description', content: () => type.built ? t(`page.business_types.${type.slug}.meta.description`) : t('page.business_types.coming_soon.subtitle') },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: () => seoTitle(t, seoTitleKey, typeName.value) },
    { property: 'og:description', content: () => type.built ? t(`page.business_types.${type.slug}.meta.description`) : t('page.business_types.coming_soon.subtitle') },
    { property: 'og:site_name', content: 'bookforme' },
    { name: 'twitter:card', content: 'summary_large_image' },
  ],
})
</script>

<template>
  <div v-if="type.built">
    <CommonBreadcrumbs :items="crumbs" />

    <LandingBusinessTypeHero :slug="type.slug" :accent="type.accent" :image="type.image" :icon="type.icon" />

    <LandingBusinessTypeCardGrid
      section-id="problems"
      :accent="type.accent"
      :badge="t(`page.business_types.${type.slug}.problems.badge`)"
      :title="t(`page.business_types.${type.slug}.problems.title`)"
      :subtitle="t(`page.business_types.${type.slug}.problems.subtitle`)"
      :items="problemItems"
    />

    <LandingBusinessTypeCardGrid
      section-id="features"
      tint
      :accent="type.accent"
      :badge="t(`page.business_types.${type.slug}.features.badge`)"
      :title="t(`page.business_types.${type.slug}.features.title`)"
      :subtitle="t(`page.business_types.${type.slug}.features.subtitle`)"
      :items="featureItems"
    />

    <LandingBusinessTypeDeepDive :slug="type.slug" :accent="type.accent" />

    <LandingBusinessTypeCardGrid
      section-id="steps"
      :accent="type.accent"
      :badge="t(`page.business_types.${type.slug}.steps.badge`)"
      :title="t(`page.business_types.${type.slug}.steps.title`)"
      :subtitle="t(`page.business_types.${type.slug}.steps.subtitle`)"
      :items="stepItems"
    />

    <LandingBusinessTypeCardGrid
      section-id="outcomes"
      tint
      :accent="type.accent"
      :badge="t(`page.business_types.${type.slug}.outcomes.badge`)"
      :title="t(`page.business_types.${type.slug}.outcomes.title`)"
      :items="outcomeItems"
    />

    <LandingBusinessTypeFaq :slug="type.slug" />

    <LandingOnePlatform />

    <LandingBusinessTypeCta :slug="type.slug" :accent="type.accent" />
  </div>

  <LandingBusinessTypeComingSoon v-else :type="type" />
</template>
